
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n/index';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { asideMenuIcons } from '@/core/helpers/config';
import { reinitializeComponents } from '@/core/plugins/keenthemes';
import { ButtonsType } from '../../core/enums/buttons.enums';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import IconMemoCircleCheckClassicLight from '@/icons/IconMemoCircleCheckClassicLight.vue';
import IconMemoCircleCheckClassicSolid from '@/icons/IconMemoCircleCheckClassicSolid.vue';
import { Actions } from '@/store/enums/StoreEnums';
import { filterMenuByCapAndPerm } from '@/core/helpers/menu-filter';

import MainMenuConfig from '@/core/config/MainMenuConfig';

type MenuMatchItem = {
  heading?: string;
  sectionTitle?: string;
  hiddenLink?: string;
  route?: string;
  sub?: MenuMatchItem[];
};

const normalizePath = (path: string) => path.replace(/\/$/, '');

export default defineComponent({
  name: 'kt-menu',

  components: {
    FontAwesomeIcon,
  },

  setup() {
    const { t, te } = useI18n();
    const route = useRoute();
    const store = useStore();
    const scrollElRef = ref<null | HTMLElement>(null);
    const isReady = ref(false);

    const reinitMenuComponents = () => {
      nextTick(() => {
        reinitializeComponents();
      });
    };

    onMounted(async () => {
      if (scrollElRef.value) {
        scrollElRef.value.scrollTop = 0;
      }

      await store.dispatch(Actions.LOAD_ABILITIES);
      isReady.value = true;
      reinitMenuComponents();
    });

    const filteredMenu = computed(() => {
      if (!isReady.value) return MainMenuConfig;

      const capDonVi: number = store.getters.capDonVi ?? 2;
      const authzModule = (store.state as {
        AuthzModule?: { permissions: Set<string> };
      }).AuthzModule;
      const permissions: Set<string> = authzModule?.permissions ?? new Set();

      return filterMenuByCapAndPerm(MainMenuConfig, { capDonVi, permissions });
    });

    watch(filteredMenu, () => {
      reinitMenuComponents();
    });

    watch(
      () => route.path,
      () => {
        reinitMenuComponents();
      }
    );

    const translate = (text) => {
      if (te(text)) {
        return t(text);
      }

      return text;
    };

    const hasSubItems = (menuItem: MenuMatchItem) => {
      return Array.isArray(menuItem.sub) && menuItem.sub.length > 0;
    };

    const isRouteMatch = (target?: string) => {
      if (!target) {
        return false;
      }

      const current = normalizePath(route.path);
      const match = normalizePath(target);

      return current === match || current.startsWith(`${match}/`);
    };

    const hasActiveChildren = (menuItem: MenuMatchItem): boolean => {
      if (isRouteMatch(menuItem.hiddenLink) || isRouteMatch(menuItem.route)) {
        return true;
      }

      return (menuItem.sub ?? []).some((child) => {
        if (child.heading && child.route) {
          return isRouteMatch(child.route);
        }

        if (child.sectionTitle) {
          return hasActiveChildren(child);
        }

        return false;
      });
    };

    return {
      filteredMenu,
      asideMenuIcons,
      translate,
      hasSubItems,
      hasActiveChildren,
      isRouteMatch,
    };
  },

  methods: {
    iconFinder(customIcon) {
      switch (customIcon) {
        case ButtonsType.FA_FILE_CIRCLE_XMARK:
          return 'fa-solid fa-file-circle-xmark';
        default:
          return 'fa-solid fa-xmark';
      }
    },

    icon(iconName: string) {
      switch (iconName) {
        case ButtonsType.MEMO_CIRCLE_CHECK_CLASSIC_LIGHT:
          return IconMemoCircleCheckClassicLight;
        case ButtonsType.MEMO_CIRCLE_CHECK_CLASSIC_SOLID:
          return IconMemoCircleCheckClassicSolid;
        default:
          return IconMemoCircleCheckClassicLight;
      }
    },
  },
});
